import { handleTetrisPhaseClick, handleChessPhaseClick, resetGameState, startPlayingGame } from './enhanced-gameCore.js';
import * as sceneModule from './scene';
import gameState from './utils/gameState.js';
import { loginWithEmail, handleAuthRedirect, isSignedIn } from './auth/auth0Client.js';
import { showLoginDialog } from './auth/loginDialog.js';
import { isLoggedIn as isKingdomLoggedIn, getLoggedInName as getKingdomName } from './auth/kingdomKey.js';
import { enterBattleFlow } from './battle/battleMode.js';
import { getSocket } from './utils/networkManager.js';

/**
 * Create a loading indicator with Russian-themed styling
 */
export function createLoadingIndicator() {
	// Remove existing loading indicator if present
	const existingIndicator = document.getElementById('loading-indicator');
	if (existingIndicator) {
		document.body.removeChild(existingIndicator);
	}

	// Create loading indicator
	const loadingIndicator = document.createElement('div');
	loadingIndicator.id = 'loading-indicator';

	// Style with Russian/chess/tetris theme
	Object.assign(loadingIndicator.style, {
		position: 'fixed',
		top: '0',
		left: '0',
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(0, 0, 0, 0.9)',
		color: '#ffcc00', // Gold text
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: '9999',
		fontFamily: 'Times New Roman, serif'
	});

	// Create animated chess piece with tetris blocks
	const animationContainer = document.createElement('div');
	Object.assign(animationContainer.style, {
		position: 'relative',
		width: '100px',
		height: '100px',
		marginBottom: '20px'
	});

	// Chess knight symbol with animation
	const chessSymbol = document.createElement('div');
	chessSymbol.innerHTML = '♞';
	Object.assign(chessSymbol.style, {
		fontSize: '80px',
		animation: 'pulse 1.5s infinite',
		color: '#ffcc00',
		textShadow: '0 0 10px rgba(255, 204, 0, 0.7)'
	});

	// Create animation keyframes
	const styleElement = document.createElement('style');
	styleElement.textContent = `
		@keyframes pulse {
			0% { transform: scale(1); }
			50% { transform: scale(1.1); }
			100% { transform: scale(1); }
		}
		@keyframes tetrisfall {
			0% { transform: translateY(-20px); opacity: 0; }
			100% { transform: translateY(0); opacity: 1; }
		}
	`;
	document.head.appendChild(styleElement);

	// Create loading text
	const loadingText = document.createElement('div');
	loadingText.textContent = 'Preparing Tetches World...';
	Object.assign(loadingText.style, {
		fontSize: '24px',
		marginBottom: '10px',
		fontWeight: 'bold',
		textShadow: '0 0 5px rgba(255, 204, 0, 0.5)'
	});

	// Create subtitle
	const subtitle = document.createElement('div');
	subtitle.textContent = 'Please wait while the pieces are arranged';
	Object.assign(subtitle.style, {
		fontSize: '16px',
		opacity: '0.8',
		marginBottom: '30px'
	});

	// Add elements to document
	animationContainer.appendChild(chessSymbol);
	loadingIndicator.appendChild(animationContainer);
	loadingIndicator.appendChild(loadingText);
	loadingIndicator.appendChild(subtitle);
	document.body.appendChild(loadingIndicator);

	return loadingIndicator;
}
export function hideError() {
	const errorElement = document.getElementById('error-message');
	if (errorElement) {
		errorElement.style.display = 'none';
	}
}


/**
 * Show error message in a styled overlay
 * @param {string} message - Error message to display
 */
export function showErrorMessage(message) {
	// Create error message container if it doesn't exist
	let errorElement = document.getElementById('error-message');
	if (!errorElement) {
		errorElement = document.createElement('div');
		errorElement.id = 'error-message';

		// Style the error message with Russian theme
		Object.assign(errorElement.style, {
			position: 'fixed',
			top: '0',
			left: '0',
			width: '100%',
			height: '100%',
			backgroundColor: 'rgba(0, 0, 0, 0.9)',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			zIndex: '1001',
			color: '#ffcc00',
			textAlign: 'center',
			fontFamily: 'Times New Roman, serif',
			padding: '20px'
		});

		document.body.appendChild(errorElement);
	}

	// Set error message with Russian-themed styling
	errorElement.innerHTML = `
		<div style="max-width: 600px; background-color: rgba(50, 0, 0, 0.8); padding: 30px; border-radius: 10px; border: 2px solid #ffcc00;">
			<h2 style="color: #ffcc00; margin-top: 0;">Error</h2>
			<p style="font-size: 18px; margin-bottom: 20px;">${message}</p>
			<button onclick="window.location.reload()" style="background: #333; color: #ffcc00; border: 1px solid #ffcc00; padding: 10px 20px; font-size: 16px; cursor: pointer; font-family: 'Times New Roman', serif;">
				Reload Page
			</button>

		</div>
	`;

	// Show the error message
	errorElement.style.display = 'flex';
}
/**
 * Create game status display
 */
export function createGameStatusDisplay(gameState) {
	console.log('Creating game status display...');

	// Create or get status container
	let statusContainer = document.getElementById('game-status');
	if (!statusContainer) {
		statusContainer = document.createElement('div');
		statusContainer.id = 'game-status';

		// Style the container with Russian theme
		Object.assign(statusContainer.style, {
			position: 'fixed',
			top: '10px',
			right: '10px',
			backgroundColor: 'rgba(0, 0, 0, 0.7)',
			color: '#ffcc00', // Gold text
			padding: '15px',
			borderRadius: '5px',
			fontFamily: 'Times New Roman, serif', // Russian-style font
			fontSize: '16px',
			zIndex: '100',
			minWidth: '220px',
			textAlign: 'center',
			border: '2px solid #ffcc00', // Gold border
			boxShadow: '0 0 10px rgba(255, 204, 0, 0.3)' // Gold glow
		});

		document.body.appendChild(statusContainer);
	}

	// Create or get controls container for debug options
	let controlsContainer = document.getElementById('debug-controls');
	if (!controlsContainer) {
		controlsContainer = document.createElement('div');
		controlsContainer.id = 'debug-controls';

		// Style the container with Russian theme
		Object.assign(controlsContainer.style, {
			position: 'fixed',
			top: '200px',
			right: '10px',
			backgroundColor: 'rgba(0, 0, 0, 0.7)',
			color: '#ffcc00', // Gold text
			padding: '10px',
			borderRadius: '5px',
			fontFamily: 'Times New Roman, serif', // Russian-style font
			fontSize: '14px',
			zIndex: '100',
			minWidth: '150px',
			border: '1px solid #ffcc00' // Gold border
		});

		// Add simplified debug controls with Russian-style buttons
		controlsContainer.innerHTML = `
			<div style="text-align: center; margin-bottom: 10px; font-weight: bold;">Game Controls</div>
			<div style="margin-bottom: 10px;">
				<button id="debug-tetris-phase" style="width: 100%; background: #333; color: #ffcc00; border: 1px solid #ffcc00; padding: 5px 10px; margin-bottom: 5px; cursor: pointer;">Tetris Phase</button>
				<button id="debug-chess-phase" style="width: 100%; background: #333; color: #ffcc00; border: 1px solid #ffcc00; padding: 5px 10px; cursor: pointer;">Chess Phase</button>
			</div>
			<div>
				<button id="debug-reset-board" style="width: 100%; background: #333; color: #ffcc00; border: 1px solid #ffcc00; padding: 5px 10px; margin-bottom: 5px; cursor: pointer;">Reset Board</button>
				<button id="debug-reset-camera" style="width: 100%; background: #333; color: #ffcc00; border: 1px solid #ffcc00; padding: 5px 10px; cursor: pointer;">Reset Camera</button>
			</div>
		`;

		document.body.appendChild(controlsContainer);

		// Add event listeners for debug controls
		document.getElementById('debug-tetris-phase').addEventListener('click', handleTetrisPhaseClick);
		document.getElementById('debug-chess-phase').addEventListener('click', handleChessPhaseClick);

		document.getElementById('debug-reset-board').addEventListener('click', () => {
			resetGameState(gameState);
			sceneModule.createBoard(boardGroup, gameState);
			updateGameStatusDisplay(gameState);
		});

		document.getElementById('debug-reset-camera').addEventListener('click', () => {
			resetCameraView(true);
		});
	}

	// Initial update
	updateGameStatusDisplay(gameState);
}
/**
 * Update the game status display
 * Uses the global gameState object to show the current status
 */
export function updateGameStatusDisplay(gameState) {
	// Allow callers to omit the parameter (many modules call `window.updateGameStatusDisplay()`)
	// while keeping the existing explicit-arg behaviour.
	const effectiveState = gameState || window.gameState;
	gameState = effectiveState;
	
	// Get status container
	const statusContainer = document.getElementById('game-status');
	
	// Skip update if container not found
	if (!statusContainer) {
		return;
	}
	
	// Default status HTML
	let statusHTML = '';
	
	// Check if we have game state
	if (!gameState) {
		statusHTML = '<div>Game ready to start</div>';
		statusContainer.innerHTML = statusHTML;
		return;
	}
	
	
	// Check various game status conditions
	if (gameState.gameOver) {
		statusHTML = `
			<div>Game Over!</div>
			<div>Player ${gameState.winner} wins!</div>
		`;
	} 
	else {
		// Get phase name - default to 'tetris' if no phase is defined
		const phase = !gameState.turnPhase || gameState.turnPhase === 'tetris' ? 'Tetris Phase' : 'Chess Phase';
		
		// Use a simplified status that focuses on current player's phase
		const playerColor = '#DD0000';  // Red for local player
		
		statusHTML = `
			<div>
				<span style="color: ${playerColor};">${phase}</span>
			</div>
		`;
		
		// Show appropriate action prompt based on the phase
		if (gameState.turnPhase === 'tetris') {
			statusHTML += `
				<div style="font-weight: bold; margin-top: 5px;">Place Your Tetromino</div>
			`;
		} else if (gameState.turnPhase === 'chess') {
			statusHTML += `
				<div style="font-weight: bold; margin-top: 5px;">Move Your Chess Piece</div>
			`;
		}
		
		// Add connected players info
		if (gameState.players) {
			const playerCount = Object.keys(gameState.players).length;
			if (playerCount > 0) {
				statusHTML += `<div style="font-size: 14px; margin-top: 5px;">${playerCount} players connected</div>`;
			}
		}
	}
	
	// Update the display
	statusContainer.innerHTML = statusHTML;
}
/**
 * Update the network status display.
 *
 * Single writer for the #network-status pill: callers (event listeners,
 * the fallback poll) all come through here so the show/hide logic stays
 * in one place. "Connected" is the expected steady state, so it only
 * flashes briefly and then gets out of the way; every other state stays
 * on screen until resolved.
 *
 * @param {string} status - 'connecting', 'connected', 'disconnected' or 'error'
 */

const NETWORK_CONNECTED_FLASH_MS = 2500;
let lastNetworkStatus = null;
let networkStatusHideTimer = null;

export function updateNetworkStatus(status) {
	const networkStatusElement = document.getElementById('network-status');

	if (!networkStatusElement) return;
	// The 5s fallback poll re-reports the same state forever — bail early
	// so "connected" doesn't re-flash every poll.
	if (status === lastNetworkStatus) return;
	lastNetworkStatus = status;

	if (networkStatusHideTimer) {
		clearTimeout(networkStatusHideTimer);
		networkStatusHideTimer = null;
	}

	// Set text and color based on status with Russian theme
	switch (status) {
		case 'connected':
			networkStatusElement.textContent = 'Connected ✓';
			networkStatusElement.style.backgroundColor = 'rgba(0, 128, 0, 0.7)';
			networkStatusElement.style.borderColor = '#ffcc00'; // Gold border
			break;
		case 'disconnected':
			networkStatusElement.textContent = 'Network: Disconnected';
			networkStatusElement.style.backgroundColor = 'rgba(255, 0, 0, 0.7)';
			networkStatusElement.style.borderColor = '#ffcc00'; // Gold border
			break;
		case 'connecting':
			networkStatusElement.textContent = 'Connecting…';
			networkStatusElement.style.backgroundColor = 'rgba(255, 165, 0, 0.7)';
			networkStatusElement.style.borderColor = '#ffcc00'; // Gold border
			break;
		default:
			networkStatusElement.textContent = `Network: ${status}`;
			networkStatusElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
			networkStatusElement.style.borderColor = '#ffcc00'; // Gold border
	}

	networkStatusElement.style.display = 'block';
	if (status === 'connected') {
		networkStatusHideTimer = setTimeout(() => {
			networkStatusElement.style.display = 'none';
			networkStatusHideTimer = null;
		}, NETWORK_CONNECTED_FLASH_MS);
	}
}
/**
 * Show a tutorial message with Russian-themed styling
 * This should appear automatically when the game loads
 * Features: scrollable content, fixed buttons, game selection options
 */
export function showTutorialMessage(startGameFunction, options = {}) {
	// Check if tutorial is already showing
	if (document.getElementById('tutorial-message')) {
		console.log('Tutorial already showing, not creating another one');
		return;
	}

	console.log('Creating tutorial message overlay');

	// Check for previous game key in localStorage
	const previousGameKey = localStorage.getItem('tetches_game_key');

	// Launch feature flags. Tetches currently runs a SINGLE shared world,
	// so the "world key" input and the cross-device "sign in to save
	// progress" path don't yet do anything meaningful (and Auth0 email
	// login is still WIP). Hide them for launch so the modal has one clear
	// call to action; flip these back on when those features are wired.
	const ENABLE_WORLD_KEY = false;
	const ENABLE_AUTH_SIGNIN = false;

	// Create tutorial message container (full screen overlay)
	const tutorialElement = document.createElement('div');
	tutorialElement.id = 'tutorial-message';

	// Style the container as a full-screen modal with flexbox
	Object.assign(tutorialElement.style, {
		position: 'fixed',
		top: '0',
		left: '0',
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(0, 0, 0, 0.85)',
		color: 'white',
		fontFamily: 'Times New Roman, serif',
		fontSize: '16px',
		zIndex: '1000',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center'
	});

	// Create the inner modal box
	const modalBox = document.createElement('div');
	Object.assign(modalBox.style, {
		display: 'flex',
		flexDirection: 'column',
		maxWidth: 'min(90%, 600px)',
		maxHeight: '90vh',
		backgroundColor: 'rgba(10, 10, 30, 0.98)',
		borderRadius: '12px',
		border: '2px solid #ffcc00',
		boxShadow: '0 0 30px rgba(255, 204, 0, 0.4)',
		overflow: 'hidden'
	});

	// Create scrollable content area
	const scrollContent = document.createElement('div');
	Object.assign(scrollContent.style, {
		flex: '1',
		overflowY: 'auto',
		overflowX: 'hidden',
		padding: '24px',
		textAlign: 'center'
	});

	// Create fixed button area at bottom
	const buttonArea = document.createElement('div');
	Object.assign(buttonArea.style, {
		padding: '16px 24px',
		borderTop: '1px solid rgba(255, 204, 0, 0.3)',
		backgroundColor: 'rgba(10, 10, 30, 1)',
		display: 'flex',
		flexDirection: 'column',
		gap: '12px'
	});

	// Add pulse animation style
	const style = document.createElement('style');
	style.id = 'tutorial-styles';
	style.textContent = `
		@keyframes pulse {
			0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 204, 0, 0.7); }
			50% { transform: scale(1.02); box-shadow: 0 0 0 8px rgba(255, 204, 0, 0); }
			100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 204, 0, 0); }
		}
		.tutorial-btn {
			padding: 14px 28px;
			background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
			color: #ffcc00;
			border: 2px solid #ffcc00;
			border-radius: 8px;
			cursor: pointer;
			font-size: 16px;
			font-weight: bold;
			font-family: 'Times New Roman', serif;
			transition: all 0.2s ease;
			width: 100%;
		}
		.tutorial-btn:hover {
			background: linear-gradient(135deg, #2a2a4e 0%, #26315e 100%);
			transform: translateY(-1px);
		}
		.tutorial-btn.primary {
			font-size: 18px;
			animation: pulse 2s infinite;
			background: linear-gradient(135deg, #2a2a1e 0%, #3a3a2e 100%);
		}
		.tutorial-btn:disabled {
			opacity: 0.6;
			cursor: default;
			animation: none;
		}
		.game-key-input {
			padding: 12px 16px;
			background: rgba(255, 255, 255, 0.1);
			border: 1px solid rgba(255, 204, 0, 0.5);
			border-radius: 6px;
			color: #fff;
			font-size: 14px;
			font-family: monospace;
			width: 100%;
			box-sizing: border-box;
		}
		.game-key-input::placeholder {
			color: rgba(255, 255, 255, 0.5);
		}
		.tutorial-divider {
			display: flex;
			align-items: center;
			margin: 12px 0;
			color: rgba(255, 204, 0, 0.6);
			font-size: 12px;
		}
		.tutorial-divider::before, .tutorial-divider::after {
			content: '';
			flex: 1;
			border-bottom: 1px solid rgba(255, 204, 0, 0.3);
		}
		.tutorial-divider span {
			padding: 0 12px;
		}
	`;
	if (!document.getElementById('tutorial-styles')) {
		document.head.appendChild(style);
	}

	// Persist the (optional) name typed into the welcome modal so the
	// world join announces the player correctly. Blank = keep whatever
	// was stored; the server auto-names brand-new guests.
	const saveTypedPlayerName = () => {
		try {
			const input = tutorialElement.querySelector('#welcome-player-name');
			const typed = (input?.value || '').trim().slice(0, 20);
			if (typed) localStorage.setItem('playerName', typed);
		} catch (_e) { /* private mode */ }
	};

	// The spectator socket connected before the player typed their
	// name, so the server-side record may still carry the auto-guest
	// name. Battle seats are named from that record, so push the
	// rename now (world joins send the name in `join_game` anyway).
	const pushNameToServer = () => {
		try {
			const socket = getSocket();
			let stored = null;
			try { stored = localStorage.getItem('playerName'); } catch (_e) { /* private mode */ }
			if (socket && stored) socket.emit('change_name', { playerName: stored });
		} catch (_e) { /* not connected yet — join_game carries the name */ }
	};

	// Function to close tutorial and start game.
	// Returns the start function's promise so callers (e.g. the battle
	// button) can sequence follow-up UI on the world join completing.
	const startGame = (gameKey = null) => {
		saveTypedPlayerName();

		// Store the game key if provided
		if (gameKey) {
			localStorage.setItem('tetches_game_key', gameKey);
		}
		
		// Remove the tutorial
		if (tutorialElement.parentNode) {
			tutorialElement.parentNode.removeChild(tutorialElement);
		}
		
		// Start the game with the key if provided
		if (typeof startGameFunction === 'function') {
			console.log('Entering world using passed function', gameKey ? `with key: ${gameKey}` : 'default shared world');
			return Promise.resolve(startGameFunction(gameKey));
		} else if (typeof window.startTetchesGame === 'function') {
			console.log('Entering world using global function');
			return Promise.resolve(window.startTetchesGame(gameKey));
		}
		console.error('No game start function available!');
		alert('Error: Could not start the game. Please refresh and try again.');
		return Promise.resolve(false);
	};

	// Build the scrollable content. The 8-bit hero carries the title;
	// everything long-form hides behind the "How to play" toggle so the
	// first screen is one picture, one line, one button. The picture is
	// capped by viewport height: at full width on a 768px-tall laptop it
	// pushed the one line and the toggle out of sight.
	scrollContent.innerHTML = `
		<img src="/img/welcome-hero.png" alt="Tetches — chess pieces and falling tetromino blocks"
			style="width: auto; max-width: 100%; max-height: 32vh; box-sizing: border-box; display: block; border-radius: 8px; image-rendering: pixelated; margin: 0 auto 12px auto; border: 1px solid rgba(255, 204, 0, 0.35);">
		<p style="margin: 0 0 14px 0; font-size: 15px; opacity: 0.9;">
			Chess meets Tetris in one huge shared world.<br>
			<span style="opacity: 0.75; font-size: 13px;">Drop blocks to grow your kingdom, then march your pieces out to capture kings.</span>
		</p>

		<button id="howto-toggle" aria-expanded="false"
			style="background: none; border: 1px solid rgba(255, 204, 0, 0.5); color: #ffcc00; border-radius: 6px; padding: 8px 16px; font-size: 14px; cursor: pointer; font-family: inherit;">
			❓ How to play
		</button>

		<div id="howto-content" style="display: none; text-align: left; margin: 14px 0 0 0; padding: 16px; background: rgba(255, 204, 0, 0.05); border-radius: 8px;">
			<ul style="line-height: 1.6; margin: 0; padding-left: 20px;">
				<li><strong>Everyone plays at once</strong> — no waiting for turns</li>
				<li><strong>Your cycle:</strong> drop a tetromino, move one chess piece, repeat</li>
				<li><strong>Tetris controls:</strong>
					<span style="color: #ffcc00;">Arrow keys</span> move,
					<span style="color: #ffcc00;">Z/X</span> rotate,
					<span style="color: #ffcc00;">Space</span> drop
				</li>
				<li><strong>Landing guide:</strong> the outline under your piece shows where it lands —
					<span style="color: #66ff88;">green will stick</span>,
					<span style="color: #ff6666;">red will dissolve</span>
					(pieces must touch your territory)
				</li>
				<li><strong>On touch devices:</strong> on-screen buttons &amp; swipes move/rotate/drop the piece</li>
				<li><strong>Chess:</strong> click (or tap) a piece → click the green circle to move</li>
				<li><strong>Goal:</strong> capture opponent kings! 👑</li>
			</ul>
			<p style="margin: 10px 0 0 0; font-style: italic; font-size: 13px; opacity: 0.85;">
				Tip: blocks grow your territory; chess pieces do the fighting.
			</p>
			<div style="margin-top: 10px; font-size: 12px; opacity: 0.75; line-height: 1.5;">
				<strong>World</strong> = the shared board everyone plays on ·
				<strong>Player Code</strong> = your identity/progress inside it
			</div>
		</div>
	`;

	// Build the button area
	let buttonHTML = '';
	
	// If player has a previous game key, show rejoin option (world keys are
	// off for the single-shared-world launch).
	if (ENABLE_WORLD_KEY && previousGameKey) {
		buttonHTML += `
			<button id="rejoin-game-btn" class="tutorial-btn primary">
				⟲ REJOIN WITH SAVED WORLD KEY
			</button>
			<div class="tutorial-divider"><span>OR</span></div>
		`;
	}
	
	// Optional name — one field on the one modal, instead of the old
	// separate name dialog that used to pop up over everything.
	let storedName = '';
	try { storedName = localStorage.getItem('playerName') || ''; } catch (_e) { /* private mode */ }
	const escapedName = storedName.replace(/[<>&"]/g, '');

	// Invite links (/?battle=CODE) surface on the battle button so the
	// invitee knows exactly what to click.
	let inviteBattleCode = null;
	try {
		const inviteParam = new URLSearchParams(window.location.search).get('battle');
		if (inviteParam && /^[A-Za-z0-9]{4,8}$/.test(inviteParam)) {
			inviteBattleCode = inviteParam.toUpperCase();
		}
	} catch (_e) { /* no URL params */ }

	// With a battle invite in the URL, joining THAT battle is the
	// headline action: the battle button renders first, biggest, and
	// Enter in the name field triggers it. PLAY NOW drops to a quiet
	// secondary option.
	const newGameIsPrimary = !(ENABLE_WORLD_KEY && previousGameKey) && !inviteBattleCode;
	const playButtonHtml = `
		<button id="new-game-btn" class="tutorial-btn ${newGameIsPrimary ? 'primary' : ''}" ${inviteBattleCode ? 'style="font-size: 14px; padding: 10px 16px;"' : ''}>
			✦ PLAY NOW
		</button>
		<div style="font-size: ${inviteBattleCode ? '11px' : '12px'}; opacity: 0.8; text-align: center; margin-top: -2px;">
			${inviteBattleCode
		? 'Or skip the battle and enter the shared world instead.'
		: 'Jump straight in — no sign-up needed, your spot is remembered on this device.'}
		</div>
	`;
	const battleButtonHtml = `
		<button id="welcome-battle-btn" class="tutorial-btn ${inviteBattleCode ? 'primary' : ''}" style="${inviteBattleCode ? '' : 'font-size: 14px; padding: 10px 16px;'}">
			${inviteBattleCode ? `⚔ JOIN BATTLE ${inviteBattleCode}` : '⚔ BATTLE A FRIEND'}
		</button>
		<div style="font-size: 11px; opacity: 0.7; text-align: center; margin-top: -4px;">
			${inviteBattleCode
		? 'You have been invited to a private battle — click to take your seat.'
		: 'Private 2-4 player arena — share a code, or fight the bots.'}
		</div>
	`;
	buttonHTML += `
		<input type="text" id="welcome-player-name" class="game-key-input"
			placeholder="Your name (optional)" maxlength="20" value="${escapedName}"
			autocomplete="nickname" style="text-align: center;">
		${inviteBattleCode ? battleButtonHtml + playButtonHtml : playButtonHtml + battleButtonHtml}
	`;

	// Account link: prominent but unmistakably optional (guests enter with
	// the big button above; this only adds cross-device persistence).
	let kingdomName = null;
	try { kingdomName = isKingdomLoggedIn() ? getKingdomName() : null; } catch (_e) { /* cookie access */ }
	if (kingdomName) {
		buttonHTML += `
			<div style="font-size: 12px; text-align: center; color: #88dd88;">
				✓ Logged in as <strong>${kingdomName.replace(/[<>&]/g, '')}</strong> — your kingdom follows you across devices.
			</div>
		`;
	} else {
		buttonHTML += `
			<div style="font-size: 12px; text-align: center; opacity: 0.85;">
				Optional: <a href="#" id="welcome-login-link" style="color: #ffcc00; text-decoration: underline;">log in</a>
				to keep your kingdom on any device.
			</div>
		`;
	}

	if (ENABLE_WORLD_KEY) {
		buttonHTML += `
			<div class="tutorial-divider"><span>OR ENTER SPECIFIC WORLD KEY</span></div>
			<div style="display: flex; gap: 8px;">
				<input type="text" id="game-key-input" class="game-key-input" 
					placeholder="Enter world key..." 
					style="flex: 1;">
				<button id="join-key-btn" class="tutorial-btn" style="width: auto; padding: 12px 20px;">
					JOIN
				</button>
			</div>
		`;
	}

	if (ENABLE_AUTH_SIGNIN) {
		buttonHTML += `
			<div class="tutorial-divider"><span>OR SIGN IN TO SAVE PROGRESS</span></div>
			<div id="auth-section">
				<button id="auth-login-btn" class="tutorial-btn" style="width: 100%;">
					✉ SIGN IN / SIGN UP
				</button>
				<p style="margin: 6px 0 0 0; font-size: 11px; color: #888; text-align: center;">
					Saves your progress across devices. Sign-in is handled securely by Auth0 — we never see your password or store your email.
				</p>
				<p id="auth-status" style="margin: 8px 0 0 0; font-size: 12px; color: #888; display: none;"></p>
			</div>
		`;
	}
	
	buttonArea.innerHTML = buttonHTML;

	// Assemble the modal
	modalBox.appendChild(scrollContent);
	modalBox.appendChild(buttonArea);
	tutorialElement.appendChild(modalBox);

	// Add the element to the document
	document.body.appendChild(tutorialElement);

	// Set up button event handlers
	const newGameBtn = tutorialElement.querySelector('#new-game-btn');
	const rejoinBtn = tutorialElement.querySelector('#rejoin-game-btn');
	const joinKeyBtn = tutorialElement.querySelector('#join-key-btn');
	const gameKeyInput = tutorialElement.querySelector('#game-key-input');

	// "How to play" starts collapsed; the toggle flips it open in place.
	const howtoToggle = tutorialElement.querySelector('#howto-toggle');
	const howtoContent = tutorialElement.querySelector('#howto-content');
	if (howtoToggle && howtoContent) {
		howtoToggle.addEventListener('click', () => {
			const isOpen = howtoContent.style.display !== 'none';
			howtoContent.style.display = isOpen ? 'none' : 'block';
			howtoToggle.setAttribute('aria-expanded', String(!isOpen));
			howtoToggle.textContent = isOpen ? '❓ How to play' : '▲ Hide the rules';
		});
	}

	// Optional account login from the welcome screen.
	const welcomeLoginLink = tutorialElement.querySelector('#welcome-login-link');
	if (welcomeLoginLink) {
		welcomeLoginLink.addEventListener('click', (event) => {
			event.preventDefault();
			let prefill = '';
			try { prefill = localStorage.getItem('playerName') || ''; } catch (_e) { /* private mode */ }
			showLoginDialog({ prefillUsername: prefill });
		});
	}

	if (newGameBtn) {
		newGameBtn.addEventListener('click', () => {
			newGameBtn.disabled = true;
			newGameBtn.textContent = 'Entering...';
			startGame(null); // Default shared world (session may restore position)
		});
	}

	// Enter in the name field = the primary action (JOIN BATTLE when
	// arriving on an invite link, PLAY NOW otherwise).
	const welcomeNameInput = tutorialElement.querySelector('#welcome-player-name');
	if (welcomeNameInput) {
		welcomeNameInput.addEventListener('keydown', (event) => {
			if (event.key !== 'Enter') return;
			event.preventDefault();
			const primaryBtn = inviteBattleCode
				? tutorialElement.querySelector('#welcome-battle-btn')
				: newGameBtn;
			if (primaryBtn) primaryBtn.click();
		});
	}

	// Battle a friend: goes straight to the battle lobby WITHOUT
	// joining the shared world — battle-only players never grow a
	// kingdom, they fight in a private arena and come back here after.
	// With an invite code in the URL the seat is claimed immediately.
	const welcomeBattleBtn = tutorialElement.querySelector('#welcome-battle-btn');
	if (welcomeBattleBtn) {
		welcomeBattleBtn.addEventListener('click', () => {
			welcomeBattleBtn.disabled = true;
			welcomeBattleBtn.textContent = inviteBattleCode ? 'Taking your seat…' : 'Opening battle lobby…';
			saveTypedPlayerName();
			pushNameToServer();
			// Hide the modal — the battle dialog takes over on top of
			// the spectator overview (no world entry happens).
			if (tutorialElement.parentNode) {
				tutorialElement.parentNode.removeChild(tutorialElement);
			}
			enterBattleFlow(inviteBattleCode)
				.catch((err) => console.warn('Battle entry failed:', err));
		});
	}

	if (rejoinBtn) {
		rejoinBtn.addEventListener('click', () => {
			rejoinBtn.disabled = true;
			rejoinBtn.textContent = 'Rejoining...';
			startGame(previousGameKey);
		});
	}

	if (joinKeyBtn && gameKeyInput) {
		const joinWithKey = () => {
			const key = gameKeyInput.value.trim();
			if (key) {
				joinKeyBtn.disabled = true;
				joinKeyBtn.textContent = '...';
				startGame(key);
			} else {
				gameKeyInput.style.borderColor = '#ff4444';
				gameKeyInput.focus();
				setTimeout(() => {
					gameKeyInput.style.borderColor = 'rgba(255, 204, 0, 0.5)';
				}, 1000);
			}
		};
		
		joinKeyBtn.addEventListener('click', joinWithKey);
		gameKeyInput.addEventListener('keypress', (e) => {
			if (e.key === 'Enter') joinWithKey();
		});
	}

	// Email sign-in via Auth0. Auth0 hosts the email entry, the one-time
	// code/link delivery, and verification on its own pages, so the game
	// never sees an email address. After sign-in Auth0 redirects back here.
	// Gated off for launch (single shared world + email login WIP); the
	// guard also keeps us from calling the CSP-restricted Auth0 SDK on
	// every page load. Flip ENABLE_AUTH_SIGNIN to re-enable.
	if (ENABLE_AUTH_SIGNIN) {
		const authLoginBtn = tutorialElement.querySelector('#auth-login-btn');
		const authStatus = tutorialElement.querySelector('#auth-status');

		const setAuthStatus = (text, color) => {
			if (!authStatus) return;
			authStatus.textContent = text || '';
			authStatus.style.color = color || '#888';
			authStatus.style.display = text ? 'block' : 'none';
		};

		if (authLoginBtn) {
			authLoginBtn.addEventListener('click', async () => {
				const originalLabel = authLoginBtn.textContent;
				authLoginBtn.disabled = true;
				try {
					// Returning player with a live session? Skip straight in.
					if (await isSignedIn()) {
						authLoginBtn.textContent = 'Entering…';
						startGame(gameKeyInput?.value.trim() || previousGameKey || null);
						return;
					}
					authLoginBtn.textContent = 'Redirecting…';
					setAuthStatus('Opening secure sign-in…', '#ffcc00');
					await loginWithEmail(gameKeyInput?.value.trim() || null);
					// loginWithRedirect navigates away; control won't return here.
				} catch (error) {
					console.error('[auth] sign-in failed to start:', error);
					setAuthStatus(error.message || 'Could not start sign-in. Please try again.', '#ff6666');
					authLoginBtn.disabled = false;
					authLoginBtn.textContent = originalLabel;
				}
			});
		}

		// Complete an Auth0 redirect (if this page load is the return leg of
		// one) and otherwise reflect any existing session in the button.
		(async () => {
			try {
				const appState = await handleAuthRedirect();
				if (appState) {
					const resumedGameKey = appState.gameKey || null;
					if (resumedGameKey) localStorage.setItem('tetches_game_key', resumedGameKey);
					setAuthStatus('✓ Signed in! Entering…', '#66ff66');
					setTimeout(() => startGame(resumedGameKey), 500);
					return;
				}
				if ((await isSignedIn()) && authLoginBtn) {
					authLoginBtn.textContent = '✓ SIGNED IN — ENTER';
					setAuthStatus('You are signed in. Click to enter.', '#66ff66');
				}
			} catch (error) {
				console.error('[auth] redirect handling failed:', error);
				setAuthStatus('Sign-in could not be completed. Please try again.', '#ff6666');
			}
		})();
	}
}
/**
 * Utility function to hide all loading elements.
 *
 * Called on every board update, so it must be quiet and cheap when
 * there's nothing left to hide (the common case after the first
 * render) — it used to log "Forcibly hiding…" in a loop forever.
 */
export function hideAllLoadingElements() {
	let hidAnything = false;

	// Hide loading screen
	const loadingElement = document.getElementById('loading');
	if (loadingElement && loadingElement.style.display !== 'none') {
		loadingElement.style.display = 'none';
		hidAnything = true;
	}

	// Remove loading indicator
	const loadingIndicator = document.getElementById('loading-indicator');
	if (loadingIndicator && loadingIndicator.parentNode) {
		loadingIndicator.parentNode.removeChild(loadingIndicator);
		hidAnything = true;
	}

	// Hide any other loading elements
	const elements = document.querySelectorAll('[id*="loading"]');
	elements.forEach(el => {
		if (el.style.display !== 'none') {
			el.style.display = 'none';
			hidAnything = true;
		}
	});

	if (hidAnything) {
		console.log('Hiding loading elements');
	}
}
/**
 * Update game ID display
 * @param {string} gameId - Game ID
 */
export function updateGameIdDisplay(gameId) {
	const gameIdDisplay = document.getElementById('game-id-display');
	if (gameIdDisplay) {
		gameIdDisplay.value = gameId;
	}
}

